@let arrivalText =
  textStore.texts()
    | filterTexts: ERelationType.arrival : EEntityType.generic
    | language;
@let departureText =
  textStore.texts()
    | filterTexts: ERelationType.departure : EEntityType.generic
    | language;
@let fromText =
  textStore.texts()
    | filterTexts: ERelationType.from : EEntityType.generic
    | language;

@let departure = departureControl();
@let arrival = arrivalControl();
@let isDisabled =
  ngControl.disabled || arrival?.disabled || departure?.disabled;
<div
  class="rounded-input box-border flex w-full text-base"
  [ngClass]="{
    'cursor-not-allowed': isDisabled,
    'bg-inherit': !isDarkMode(),
  }"
  [ngStyle]="{
    backgroundColor: inputBackground(),
  }">
  @let hasError =
    (ngControl.errors && ngControl.touched) ||
    (arrival && arrival.errors && (arrival.touched || ngControl.touched)) ||
    (departure && departure.errors && (departure.touched || ngControl.touched));

  <div class="rounded-input relative w-full bg-inherit! text-base">
    <div class="relative bg-inherit">
      <div
        [tabindex]="isDisabled ? '-1' : '0'"
        class="h-12.5 border-input-ring border-input rounded-input peer p-input box-border flex w-full cursor-pointer flex-row items-center bg-transparent placeholder-transparent focus:ring-0 focus:outline-none"
        [ngClass]="{
          'border-input-error focus:border-input-focus': hasError,
          'border-input-ring focus:border-input-focus': !hasError,
          'pointer-events-none opacity-60': isDisabled,
          'text-standard-text-dark': corporateDesignService.isDarkModeActive(),
          'text-standard-text': !corporateDesignService.isDarkModeActive(),
        }"
        [ngStyle]="{
          borderRadius: borderRounding(),
          borderColor: borderAndLabelColor(),
        }"
        [id]="id()"
        (keydown.space)="$event.preventDefault()"
        (click)="showCalendar.set(!showCalendar()); isFocused.set(true)"
        [attr.data-testid]="id() | appendTestId: 'input'">
        <div class="flex flex-row items-center gap-6">
          @if (departure && departure.value) {
            <div>
              <span>{{ departureText }}: </span>
              <span>{{ departure.value | date: DATE_FORMAT }}, </span>
              <span>{{ fromText }} </span>
              <span>{{ departure.value | date: TIME_FORMAT_SHORT }}</span>
            </div>
          }
          @if (arrival && arrival.value) {
            <div>
              <span>{{ arrivalText }}: </span>
              <span>{{ arrival.value | date: DATE_FORMAT }}, </span>
              <span>{{ fromText }} </span>
              <span>{{ arrival.value | date: TIME_FORMAT_SHORT }}</span>
            </div>
          }
        </div>
      </div>
      <label
        [attr.data-testid]="id() | appendTestId: 'label'"
        class="peer-placeholder-shown:text-input-label peer-focus:text-input-focus pointer-events-none absolute -top-2 right-4 left-4 z-2 w-fit max-w-[calc(100%-2rem)] cursor-text overflow-hidden bg-inherit text-sm overflow-ellipsis whitespace-nowrap transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-sm"
        [ngClass]="{
          'text-input-error': hasError,
          'text-input-label/60': isDisabled,
          'text-input-focus': isFocused(),
          'text-standard-text-dark': !hasError && corporateDesignService.isDarkModeActive(),
          'text-standard-text': !corporateDesignService.isDarkModeActive(),
        }"
        [ngStyle]="{color: borderAndLabelColor()}"
        [for]="id()">
        {{ formattedLabel() }}
      </label>
      @if (!ngControl.control!.disabled) {
          <div class="text-input-label absolute top-3 right-4 flex items-center gap-3 text-lg">
            @if (ngControl.control?.value) {
              <button
                type="button"
                aria-label="Clear"
                class="hover:bg-standard-hover-main/30 hover:border-standard-text-secondary focus:bg-standard-hover-main/30 focus:border-standard-text-secondary flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm border-1 border-transparent focus:outline-none"
                (keyup.enter)="clear(); $event.stopPropagation();"
                (click)="clear(); $event.stopPropagation();">
                <span class="material-symbols-rounded">close</span>
              </button>
            }
            <button
              type="button"
              aria-label="Datum auswählen"
              [id]="id() | appendTestId: 'calendar-icon'"
              [attr.data-testid]="id() | appendTestId: 'calendar-icon'"
              class="hover:bg-standard-hover-main/30 hover:border-standard-text-secondary focus:bg-standard-hover-main/30 focus:border-standard-text-secondary flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm border-1 border-transparent focus:outline-none"
              (click)="showCalendar.set(!showCalendar()); $event.stopPropagation();">
              <span class="material-symbols-rounded">calendar_month</span>
            </button>
          </div>
        }
    </div>
    @if (showCalendar()) {
      <div
        [id]="'calendar-backdrop'"
        class="black/25 flex-direction fixed top-0 right-0 bottom-0 left-0 z-999 flex w-full items-center justify-center">
        <div
          [ngClass]="{
            'bg-primary-on': !isDarkMode(),
          }"
          [ngStyle]="{
            color: textColor(),
            borderRadius: borderRounding(),
            background: isDarkMode()
              ? corporateDesign().colorAdmixtures.dark_surface_01
              : '',
          }"
          class="border-input-ring max-h-full overflow-y-scroll rounded-lg border-1 bg-inherit md:min-w-96"
          [id]="id() | appendTestId: 'calendar'">
          <div
            class="flex flex-row justify-between px-4 pt-5"
            [id]="id() | appendTestId: 'calendar-header'">
            <div
              [id]="id() | appendTestId: 'calendar-headline'"
              class="text-2xl font-semibold">
              {{ headline() }}
            </div>
            <span
              tabIndex="0"
              class="material-symbols-rounded cursor-pointer"
              [id]="id() | appendTestId: 'calendar-close-icon'"
              (click)="showCalendar.set(false); resetDate()"
              (keydown.enter)="showCalendar.set(false); resetDate()">
              close
            </span>
          </div>
          @if (arrival && departure) {
            <div
              class="flex flex-row px-4 pt-4"
              [id]="
                id() | appendTestId: 'calendar-arrival-departure-container'
              ">
              <aw-button
                class="w-full"
                type="button"
                [flattenRightSide]="true"
                (click)="editDepartureDate()"
                (keydown.enter)="editDepartureDate()"
                [style]="
                  departureOrArrival.value === 'departure'
                    ? 'primary'
                    : 'primary-alt'
                "
                [id]="id() | appendTestId: 'calendar-departure'">
                {{ departureText }}
              </aw-button>
              <aw-button
                class="w-full"
                type="button"
                [flattenLeftSide]="true"
                (click)="editArrivalDate()"
                (keydown.enter)="editArrivalDate()"
                [style]="
                  departureOrArrival.value === 'arrival'
                    ? 'primary'
                    : 'primary-alt'
                "
                [id]="id() | appendTestId: 'calendar-arrival'">
                {{ arrivalText }}
              </aw-button>
            </div>
          }

          <aw-calendar
            [label]="label()"
            [borderRounding]="borderRounding()"
            [showTimePicker]="showTimePicker()"
            [id]="id() | appendTestId: 'calendar'"
            [parentId]="id()"
            [date]="ngControl!.value"
            [min]="min()"
            [max]="max()"
            (onCloseCalendar)="closeCalendar()"
            (onDateChange)="onDateChange($event)"
            (onDateConfirm)="onDateConfirm($event)">
            <div
              class="flex flex-row flex-wrap gap-2 pt-5"
              [id]="id() | appendTestId: 'time-options'">
              <aw-button
                type="button"
                (click)="pickNow()"
                (keydown.enter)="pickNow()"
                [style]="'primary-alt'"
                [id]="id() | appendTestId: 'time-option-now'">
                {{
                  textStore.texts()
                    | filterTexts: ERelationType.now : EEntityType.generic
                    | language
                }}
              </aw-button>
              <aw-button
                type="button"
                (click)="pickIn15Minutes()"
                (keydown.enter)="pickIn15Minutes()"
                [style]="'primary-alt'"
                [id]="id() | appendTestId: 'time-option-in-15-minutes'">
                {{
                  textStore.texts()
                    | filterTexts
                      : ERelationType.in15Minutes
                      : EEntityType.generic
                    | language
                }}</aw-button
              >
              <aw-button
                type="button"
                (click)="pickIn60Minutes()"
                (keydown.enter)="pickIn60Minutes()"
                [style]="'primary-alt'"
                [id]="id() | appendTestId: 'time-option-in-60-minutes'">
                {{
                  textStore.texts()
                    | filterTexts
                      : ERelationType.in60Minutes
                      : EEntityType.generic
                    | language
                }}
              </aw-button>
            </div>
          </aw-calendar>
        </div>
      </div>
    }
    @if (hasError && !errorTemplate()) {
      <aw-error-message [attr.data-testid]="id() | appendTestId: 'error'">
        <ng-content></ng-content>
      </aw-error-message>
    }
    @if (hasError && errorTemplate()) {
      <aw-error-message [attr.data-testid]="id() | appendTestId: 'error'">
        <ng-container *ngTemplateOutlet="errorTemplate()!"></ng-container>
      </aw-error-message>
    }
  </div>
</div>
